import {
  DataGridCell,
  DataGridDateCell,
  getEntity,
} from "@components/common/data-grid/adm-data-grid-utils";
import { AdmCompany, AllMemberTypes } from "@custom-types/base-types";
import { Box, Grid } from "@mui-wrapper";
import { EntityCellParams } from "@components/common/data-grid/adm-data-grid-types";
import { getMemberDisplayName } from "@utils/data-display";
import { isAdmMember } from "@utils/type-guards";
import { MAXIMUM_NUMBER_OF_COMPANIES_IN_TABLE } from "@utils/ui-utils";
import { ReactNode, useMemo, useState } from "react";
import { useAppNavigation } from "@router/use-app-navigation";
import { Chip, Tooltip, useToast } from "@flat-ui";
import {
  GeneralProfileEvents,
  GeneralTableEvents,
  MainPageEvents,
  MemberEvents,
} from "@utils/track-event/track-event-list";
import { useTrackEvent } from "@utils/track-event/use-track-event";
import { TableType } from "@utils/track-event/event-types";
import { ResendInvitationToWorkspace } from "@components/resend-invitation-to-workspace";
import { useSubjectClipboard } from "@hooks/use-subject-clipboard";
import { useResendInvitationToWorkspace } from "@components/use-resend-invitation-to-workspace";
import { useGenerateRoutes } from "@router/router-helper";
import { DataGridOptionCell } from "@components/common/data-grid/data-grid-option-cell";
import { DeleteMemberFromCompany } from "@components/delete-member/delete-member-from-company";
import { UiUtils, UtilsTypes } from "@stellar/utils";
import { FaroCoreApiUtils } from "@stellar/core-api-client";

/** Return member name cell in data-grid */
export function MemberNameCell(
  params: EntityCellParams<AllMemberTypes>
): ReactNode {
  const entity = getEntity(params);
  const memberDisplayName = getMemberDisplayName(entity);
  return (
    <DataGridCell
      content={memberDisplayName}
      tooltip={`${memberDisplayName} (${entity.identity})`}
    />
  );
}

/** Return member email cell in data-grid */
export function MemberEmailCell(
  params: EntityCellParams<AllMemberTypes>
): ReactNode {
  return <DataGridCell content={getEntity(params).email} />;
}

/** Return member workspace cell in data-grid. It shows a limited number of companies and the rest is hidden behind a tooltip */
export function MemberWorkspaceCell(
  params: EntityCellParams<AllMemberTypes>
): ReactNode {
  const { trackEvent } = useTrackEvent();
  const { navigateToProfilePage } = useAppNavigation();
  const entity = getEntity(params);
  if (!("companies" in entity) || entity.companies.length === 0) {
    return UiUtils.DASH;
  }
  const companies = entity.companies;
  const visible = companies.slice(0, MAXIMUM_NUMBER_OF_COMPANIES_IN_TABLE);
  const hiddenCount = companies.length - visible.length;

  return (
    <Grid sx={{ gap: 0.5, flexWrap: "nowrap", overflow: "hidden" }}>
      {visible.map(({ id, name }) => (
        <Chip
          textTransform="none"
          key={id}
          label={name}
          onClick={(event) => {
            event.stopPropagation();
            trackEvent({
              name: MainPageEvents.openItem,
              props: {
                target: "COMPANY",
                location: "search-members-table",
              },
            });

            navigateToProfilePage({
              subjectType: "COMPANY",
              subjectId: id,
            });
          }}
          color="primary"
          variant="outlined"
        />
      ))}

      {hiddenCount > 0 && (
        <Tooltip
          title={companies.map(({ name }) => name).join(", ")}
          placement="top"
        >
          <Box
            component="span"
            sx={{ ml: "5px" }}
          >{`+${hiddenCount} more`}</Box>
        </Tooltip>
      )}
    </Grid>
  );
}

/** Return member createdAt cell in data-grid */
export function MemberCreatedAtCell(
  params: EntityCellParams<AllMemberTypes>
): ReactNode {
  const entity = getEntity(params);
  return isAdmMember(entity) ? (
    <DataGridDateCell date={entity.createdAt} />
  ) : (
    <DataGridCell content={UiUtils.DASH} />
  );
}

/** Return member role in workspace cell in data-grid */
export function MemberRoleInWorkspaceCell(
  params: EntityCellParams<AllMemberTypes>
): ReactNode {
  const entity = getEntity(params);
  return isAdmMember(entity) ? (
    <DataGridCell content={UiUtils.DASH} />
  ) : (
    <DataGridCell
      content={FaroCoreApiUtils.Workspace.formatUserWorkspaceRole(entity.role)}
    />
  );
}

/** Return member authentication type in workspace cell in data-grid */
export function MemberAuthenticationTypeCell(
  params: EntityCellParams<AllMemberTypes>
): ReactNode {
  const entity = getEntity(params);
  return isAdmMember(entity) ? (
    <DataGridCell content={UiUtils.DASH} />
  ) : (
    <DataGridCell content={entity.authTypeInfo.name} />
  );
}

/** Return member status cell in data-grid */
export function MemberStatusCell(
  params: EntityCellParams<AllMemberTypes>,
  location: TableType
): ReactNode {
  const member = getEntity(params);
  const statusChipProps = useMemo(() => {
    return FaroCoreApiUtils.Member.isMemberActive(member)
      ? {
          label: UtilsTypes.MemberStatus.active,
          color: "primary" as const,
          tooltip: "The invited member successfully created an account",
        }
      : {
          label: UtilsTypes.MemberStatus.pending,
          color: "warning" as const,
          tooltip: "The invited member has not yet created an account",
        };
  }, [member]);

  return (
    <>
      <Chip {...statusChipProps} />
      <ResendInvitationToWorkspace member={member} location={location} />
    </>
  );
}

interface MemberOptionCellProps {
  /** The company which will be used in some of the options in this cell */
  company?: AdmCompany;

  /** The params of the cell which contains the member entity */
  params: EntityCellParams<AllMemberTypes>;

  /** The location string to be used in tracking events triggered from the options in this cell */
  location: TableType;
}

/** Return member option cell in data-grid */
export function MemberOptionCell({
  params,
  location,
  company,
}: MemberOptionCellProps): ReactNode {
  const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false);
  const { trackEvent } = useTrackEvent();
  const { openToast } = useToast();
  const { copyMemberInfo } = useSubjectClipboard();
  const { generateMemberRoute } = useGenerateRoutes();

  const entity = getEntity(params);
  const { onResendInvitationClick, shouldHideResendInvitation } =
    useResendInvitationToWorkspace({
      member: entity,
      location,
    });

  return (
    <>
      <DataGridOptionCell
        item={entity}
        menuItems={[
          {
            label: "Open member profile",
            onSelect: () => {
              trackEvent({
                name: MainPageEvents.openItem,
                props: {
                  target: "USER",
                  location,
                },
              });
            },
            getHrefUrl: (entity) =>
              generateMemberRoute({
                memberId: entity.identity,
              }).pathname,
            shouldShow: FaroCoreApiUtils.Member.isMemberActive,
          },
          {
            label: "Copy member ID",
            onSelect: async () => {
              trackEvent({
                name: GeneralTableEvents.copyRowIdToClipboard,
                props: {
                  location,
                },
              });
              await navigator.clipboard.writeText(entity.identity);
              openToast({
                title: "Member ID copied to clipboard!",
              });
            },
          },
          {
            label: "Copy member details",
            onSelect: async () => {
              trackEvent({
                name: GeneralProfileEvents.copyDetailsToClipboard,
                props: {
                  target: "USER",
                  location,
                },
              });
              await copyMemberInfo(entity);
              openToast({
                title: "Member info copied to clipboard!",
              });
            },
            shouldShow: FaroCoreApiUtils.Member.isMemberActive,
          },
          {
            label: "Send email to user",
            onSelect: () => {
              trackEvent({
                name: MemberEvents.sendEmailToMember,
              });
              globalThis.location.href = `mailto:${entity.email}`;
            },
          },
          {
            label: "Resend invitation",
            onSelect: onResendInvitationClick,
            shouldShow: () => !shouldHideResendInvitation,
            permission: "member.resendInvitation",
          },
          {
            label: "Delete Member from workspace",
            onSelect: () => {
              if (company) {
                trackEvent({
                  name: MemberEvents.deleteMemberFromWorkspace,
                  props: {
                    memberId: entity.identity,
                    companyId: company.id,
                    location,
                  },
                });
                setIsDialogOpen(true);
              }
            },
            shouldShow: () => !!company,
            permission: "member.removeFromWorkspace",
            isDangerous: true,
          },
        ]}
      />
      {company && (
        <DeleteMemberFromCompany
          member={entity}
          company={company}
          isOpen={isDialogOpen}
          onClose={() => setIsDialogOpen(false)}
          location={location}
        />
      )}
    </>
  );
}
